import type {
  SaveTaskMonitoringItem,
  TaskMonitoringActivity,
  TaskMonitoringPhase,
  TaskMonitoringTask,
  TaskMonitoringTree,
} from "../../../api/TaskMonitoring";
import type { StatusTone } from "../shared/statusColors";

// Pure helpers for the Task Monitoring settings page. Nothing here touches
// React or the network, so the rules the page relies on (what counts as a
// change, what makes a window invalid, what a row's status is on a date) can
// be read — and tested — in one place.

// ---------------------------------------------------------------- normalise

const str = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);
const num = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// gRPC omits false / 0 / '' on the way back, so an untracked task arrives with
// no `tracked` key at all and a phase with no activities has no `activities`.
// Every consumer downstream assumes the full shape, so fill it here, once.
export const normalizeTaskMonitoringTree = (raw: any): TaskMonitoringTree => ({
  projectId: num(raw?.projectId),
  asOfDate: str(raw?.asOfDate),
  taskCount: num(raw?.taskCount),
  trackedCount: num(raw?.trackedCount),
  activeOnDateCount: num(raw?.activeOnDateCount),
  trackedWithoutNormCount: num(raw?.trackedWithoutNormCount),
  activitiesWithTrackedCount: num(raw?.activitiesWithTrackedCount),
  phases: (raw?.phases || []).map(
    (phase: any): TaskMonitoringPhase => ({
      phaseId: num(phase?.phaseId),
      phaseName: str(phase?.phaseName),
      phaseSequenceId: str(phase?.phaseSequenceId),
      taskCount: num(phase?.taskCount),
      trackedCount: num(phase?.trackedCount),
      activeOnDateCount: num(phase?.activeOnDateCount),
      activities: (phase?.activities || []).map(
        (activity: any): TaskMonitoringActivity => ({
          activityId: num(activity?.activityId),
          activityName: str(activity?.activityName),
          activitySequenceId: str(activity?.activitySequenceId),
          taskCount: num(activity?.taskCount),
          trackedCount: num(activity?.trackedCount),
          activeOnDateCount: num(activity?.activeOnDateCount),
          normedTaskCount: num(activity?.normedTaskCount),
          tasks: (activity?.tasks || []).map(
            (task: any): TaskMonitoringTask => ({
              taskId: num(task?.taskId),
              taskSequenceId: str(task?.taskSequenceId),
              internalTaskNumber: str(task?.internalTaskNumber),
              taskName: str(task?.taskName),
              workQuantity: str(task?.workQuantity),
              workUomName: str(task?.workUomName),
              taskStatus: str(task?.taskStatus),
              hasExecutionNorm: task?.hasExecutionNorm === true,
              materialLineCount: num(task?.materialLineCount),
              manpowerLineCount: num(task?.manpowerLineCount),
              machineryLineCount: num(task?.machineryLineCount),
              tracked: task?.tracked === true,
              activeFrom: str(task?.activeFrom),
              activeTo: str(task?.activeTo),
              remarks: str(task?.remarks),
              trackedOnDate: task?.trackedOnDate === true,
              monitoringId: num(task?.monitoringId),
            })
          ),
        })
      ),
    })
  ),
});

// ------------------------------------------------------------------- drafts

// The four editable fields of one task's tracking row. The page keeps a map of
// these keyed by taskId holding ONLY rows that differ from what the server
// sent — so `Object.keys(edits).length` is literally "n changes".
export interface MonitoringDraft {
  tracked: boolean;
  activeFrom: string; // 'YYYY-MM-DD' or ''
  activeTo: string; // '' = open-ended
  remarks: string;
}

export type MonitoringEdits = Record<number, MonitoringDraft>;

export const draftOf = (task: TaskMonitoringTask): MonitoringDraft => ({
  tracked: task.tracked,
  activeFrom: task.activeFrom,
  activeTo: task.activeTo,
  remarks: task.remarks,
});

export const sameDraft = (a: MonitoringDraft, b: MonitoringDraft): boolean =>
  a.tracked === b.tracked &&
  a.activeFrom === b.activeFrom &&
  a.activeTo === b.activeTo &&
  a.remarks === b.remarks;

/** The row as the user currently sees it: the pending edit, else the saved row. */
export const effectiveDraft = (
  task: TaskMonitoringTask,
  edits: MonitoringEdits
): MonitoringDraft => edits[task.taskId] ?? draftOf(task);

/**
 * Apply `mutate` to every listed task and return the next edits map. A result
 * equal to the saved row is DROPPED from the map, so toggling a switch on and
 * back off leaves zero changes rather than a no-op change.
 */
export const applyDraftEdits = (
  edits: MonitoringEdits,
  tasks: TaskMonitoringTask[],
  mutate: (current: MonitoringDraft, task: TaskMonitoringTask) => MonitoringDraft
): MonitoringEdits => {
  const next: MonitoringEdits = { ...edits };
  tasks.forEach((task) => {
    const updated = mutate(edits[task.taskId] ?? draftOf(task), task);
    if (sameDraft(updated, draftOf(task))) delete next[task.taskId];
    else next[task.taskId] = updated;
  });
  return next;
};

/** Switch a row on: keep any window it already has, else start it on the as-of date. */
export const trackOn = (current: MonitoringDraft, defaultFrom: string): MonitoringDraft => ({
  ...current,
  tracked: true,
  activeFrom: current.activeFrom || defaultFrom,
});

/**
 * Switch a row off. A row that was never tracked reverts wholesale (its
 * defaulted window was ours, not the user's); a row that WAS tracked keeps its
 * window so switching back on restores it unchanged.
 */
export const trackOff = (
  current: MonitoringDraft,
  task: TaskMonitoringTask
): MonitoringDraft => (task.tracked ? { ...current, tracked: false } : draftOf(task));

// --------------------------------------------------------------- validation

export type DraftProblem = "missingFrom" | "toBeforeFrom";

// Mirrors the server guards in save-task-monitoring.handler.ts exactly:
// activeFrom is required when tracked, and activeTo (when set) must not be
// before activeFrom. Untracked rows carry no window and cannot be invalid.
export const draftProblem = (draft: MonitoringDraft): DraftProblem | null => {
  if (!draft.tracked) return null;
  if (!draft.activeFrom) return "missingFrom";
  if (draft.activeTo && draft.activeTo < draft.activeFrom) return "toBeforeFrom";
  return null;
};

export const invalidTaskIds = (edits: MonitoringEdits): number[] =>
  Object.entries(edits)
    .filter(([, draft]) => draftProblem(draft) !== null)
    .map(([taskId]) => Number(taskId));

/** Only the changed rows, in the shape the save endpoint wants. */
export const toSaveItems = (edits: MonitoringEdits): SaveTaskMonitoringItem[] =>
  Object.entries(edits).map(([taskId, draft]) => ({
    taskId: Number(taskId),
    tracked: draft.tracked,
    // The server ignores the window on an untrack; send it blank so the
    // payload reads the way the row will be stored.
    activeFrom: draft.tracked ? draft.activeFrom : "",
    activeTo: draft.tracked ? draft.activeTo : "",
    remarks: draft.tracked ? draft.remarks.trim() : "",
  }));

// ------------------------------------------------------------------- status

export type MonitoringStatusKey = "active" | "scheduled" | "expired" | "off" | "noWindow";

// 'YYYY-MM-DD' strings compare correctly as plain strings, which is why every
// date on this page stays a string until it reaches a DatePicker.
export const windowCovers = (draft: MonitoringDraft, asOf: string): boolean =>
  draft.tracked &&
  !!draft.activeFrom &&
  draft.activeFrom <= asOf &&
  (!draft.activeTo || draft.activeTo >= asOf);

export const monitoringStatus = (
  draft: MonitoringDraft,
  asOf: string
): MonitoringStatusKey => {
  if (!draft.tracked) return "off";
  if (!draft.activeFrom) return "noWindow";
  if (draft.activeFrom > asOf) return "scheduled";
  if (draft.activeTo && draft.activeTo < asOf) return "expired";
  return "active";
};

// Text/fg colours per the design tokens: green #237804, amber #d46b08, and the
// grey/muted families from statusColors. Built as tones so GenericStatusPill
// renders them — never a solid antd Tag for status.
export const MONITORING_STATUS_TONES: Record<MonitoringStatusKey, StatusTone> = {
  active: { dot: "#52c41a", text: "#237804", bg: "#f6ffed" },
  scheduled: { dot: "#fa8c16", text: "#d46b08", bg: "#fff7e6" },
  noWindow: { dot: "#fa8c16", text: "#d46b08", bg: "#fff7e6" },
  expired: { dot: "#595959", text: "#434343", bg: "#f0f0f0" },
  off: { dot: "#8094ae", text: "#526484", bg: "#f0f2f5" },
};

// ----------------------------------------------------------------- the tree

export type MonitoringFilter = "all" | "tracked" | "untracked" | "missingNorm";

export interface PhaseRow {
  key: string; // 'P-<phaseId>'
  level: "phase";
  phase: TaskMonitoringPhase;
  tasks: TaskMonitoringTask[]; // every task beneath, for group operations
  children: ActivityRow[];
}

export interface ActivityRow {
  key: string; // 'A-<activityId>'
  level: "activity";
  activity: TaskMonitoringActivity;
  tasks: TaskMonitoringTask[];
  children: TaskRow[];
}

export interface TaskRow {
  key: string; // 'T-<taskId>'
  level: "task";
  task: TaskMonitoringTask;
}

export type TreeRow = PhaseRow | ActivityRow | TaskRow;

const includes = (haystack: string, needle: string) =>
  haystack.toLowerCase().includes(needle);

/**
 * Filter + shape the server tree into antd rows. Prefixed keys so a phaseId
 * can never collide with an activityId or taskId in the expanded-keys set.
 *
 * The search and the segmented filter evaluate against the SAVED state, not
 * the pending edits: under the "Untracked" filter a planner flips ten rows on
 * and then sets their dates — rows that vanished the moment they were
 * switched could never be given a window.
 */
export const buildMonitoringRows = (
  tree: TaskMonitoringTree | null,
  search: string,
  filter: MonitoringFilter,
  phaseId?: number,
  activityId?: number
): PhaseRow[] => {
  if (!tree) return [];
  const needle = search.trim().toLowerCase();
  const rows: PhaseRow[] = [];
  tree.phases.forEach((phase) => {
    if (phaseId && phase.phaseId !== phaseId) return;
    const activities: ActivityRow[] = [];
    phase.activities.forEach((activity) => {
      if (activityId && activity.activityId !== activityId) return;
      const tasks = activity.tasks.filter((task) => {
        if (filter === "tracked" && !task.tracked) return false;
        if (filter === "untracked" && task.tracked) return false;
        if (filter === "missingNorm" && task.hasExecutionNorm) return false;
        if (!needle) return true;
        return (
          includes(task.taskSequenceId, needle) ||
          includes(task.taskName, needle) ||
          includes(activity.activityName, needle) ||
          includes(activity.activitySequenceId, needle) ||
          includes(phase.phaseName, needle)
        );
      });
      if (!tasks.length) return;
      activities.push({
        key: `A-${activity.activityId}`,
        level: "activity",
        activity,
        tasks,
        children: tasks.map((task) => ({ key: `T-${task.taskId}`, level: "task", task })),
      });
    });
    if (!activities.length) return;
    rows.push({
      key: `P-${phase.phaseId}`,
      level: "phase",
      phase,
      tasks: activities.flatMap((row) => row.tasks),
      children: activities,
    });
  });
  return rows;
};

export const groupKeysOf = (rows: PhaseRow[]): string[] =>
  rows.flatMap((phase) => [phase.key, ...phase.children.map((activity) => activity.key)]);

export const allTasksOf = (tree: TaskMonitoringTree | null): TaskMonitoringTask[] =>
  tree
    ? tree.phases.flatMap((phase) =>
        phase.activities.flatMap((activity) => activity.tasks)
      )
    : [];

// ------------------------------------------------------------------- counts

export interface MonitoringCounts {
  taskCount: number;
  trackedCount: number;
  activeOnDateCount: number;
  activitiesWithTrackedCount: number;
  trackedWithoutNormCount: number;
}

// Computed from the EFFECTIVE drafts so the tiles preview a pending change;
// with no edits they equal the server's own counts exactly.
export const countMonitoring = (
  tree: TaskMonitoringTree | null,
  edits: MonitoringEdits,
  asOf: string
): MonitoringCounts => {
  const counts: MonitoringCounts = {
    taskCount: 0,
    trackedCount: 0,
    activeOnDateCount: 0,
    activitiesWithTrackedCount: 0,
    trackedWithoutNormCount: 0,
  };
  if (!tree) return counts;
  tree.phases.forEach((phase) =>
    phase.activities.forEach((activity) => {
      let activityTracked = 0;
      activity.tasks.forEach((task) => {
        const draft = effectiveDraft(task, edits);
        counts.taskCount += 1;
        if (!draft.tracked) return;
        counts.trackedCount += 1;
        activityTracked += 1;
        if (windowCovers(draft, asOf)) counts.activeOnDateCount += 1;
        if (!task.hasExecutionNorm) counts.trackedWithoutNormCount += 1;
      });
      if (activityTracked > 0) counts.activitiesWithTrackedCount += 1;
    })
  );
  return counts;
};

/** "n of m tracked · k active" for a group row, over its (filtered) tasks. */
export const groupSummary = (
  tasks: TaskMonitoringTask[],
  edits: MonitoringEdits,
  asOf: string
): { tracked: number; total: number; active: number } => {
  let tracked = 0;
  let active = 0;
  tasks.forEach((task) => {
    const draft = effectiveDraft(task, edits);
    if (!draft.tracked) return;
    tracked += 1;
    if (windowCovers(draft, asOf)) active += 1;
  });
  return { tracked, total: tasks.length, active };
};
